import { useEffect, useState } from "react";
import { useAuth, api } from "../App";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  TrendingDown,
  Store,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Settings,
  LogOut,
  RefreshCw,
  ExternalLink,
  Loader2,
  ArrowRight,
  Building2,
  LayoutGrid
} from "lucide-react";

const PortfolioDashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    fetchPortfolio();
    fetchBillingStatus();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await api.get("/portfolio/overview");
      setPortfolio(res.data);
    } catch (error) {
      console.error("Failed to fetch portfolio:", error);
      toast.error("Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingStatus = async () => {
    try {
      const res = await api.get("/billing/status");
      setBillingStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch billing status:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPortfolio();
    setRefreshing(false);
    toast.success("Portfolio refreshed");
  };

  const formatCurrency = (value, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "critical":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
            <AlertTriangle className="w-3 h-3" />
            Critical
          </span>
        );
      case "warning":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <AlertCircle className="w-3 h-3" />
            Warning
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
            <CheckCircle2 className="w-3 h-3" />
            Healthy
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/dashboard" />
            <span className="hidden md:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4F34A]/10 text-[#D4F34A] text-xs font-medium border border-[#D4F34A]/20">
              <Building2 className="w-3 h-3" />
              Agency Portfolio
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-text-muted hover:text-white">
                <LayoutGrid className="w-4 h-4 mr-2" />
                Single Store View
              </Button>
            </Link>
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-text-muted hover:text-white">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={logout}
              className="text-text-muted hover:text-white"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6 md:px-12 max-w-7xl mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#121212] border-[#262626]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-muted">Total Stores</span>
                <Store className="w-5 h-5 text-[#D4F34A]" />
              </div>
              <p className="text-3xl font-bold font-heading">{portfolio?.total_stores || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#121212] border-[#262626]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-muted">Total Impact</span>
                <DollarSign className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-3xl font-bold font-heading text-red-400">
                {formatCurrency(portfolio?.total_estimated_impact || 0)}
              </p>
              <p className="text-xs text-text-muted mt-1">Potential weekly loss</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#121212] border-[#262626]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-muted">Critical Issues</span>
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-3xl font-bold font-heading text-red-400">
                {portfolio?.critical_count || 0}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#121212] border-[#262626]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-muted">Healthy Stores</span>
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-3xl font-bold font-heading text-green-400">
                {portfolio?.healthy_count || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Header Row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-heading">Client Stores</h1>
            <p className="text-text-muted text-sm">
              Showing {portfolio?.stores?.length || 0} connected stores
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-[#262626] hover:bg-white/5"
            data-testid="refresh-portfolio-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Store List */}
        {!portfolio?.stores?.length ? (
          <Card className="bg-[#121212] border-[#262626]">
            <CardContent className="p-12 text-center">
              <Store className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Stores Connected</h3>
              <p className="text-text-muted mb-6">
                Connect your first client store to start monitoring profit leaks.
              </p>
              <Link to="/dashboard">
                <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full">
                  Connect a Store
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {portfolio.stores.map((store) => (
              <Card 
                key={store.store_id}
                className={`bg-[#121212] border-[#262626] hover:border-white/20 transition-colors ${
                  store.status === "critical" ? "border-l-4 border-l-red-500" : 
                  store.status === "warning" ? "border-l-4 border-l-yellow-500" : ""
                }`}
                data-testid={`store-card-${store.store_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Store Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold truncate">{store.shop_domain}</h3>
                        {getStatusBadge(store.status)}
                      </div>
                      <p className="text-sm text-text-muted truncate">
                        {store.insight_title || "Awaiting analysis"}
                      </p>
                      {store.insight_summary && (
                        <p className="text-xs text-text-muted/70 mt-1 truncate">
                          {store.insight_summary}
                        </p>
                      )}
                    </div>

                    {/* Impact & Actions */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className={`text-lg font-bold font-heading ${
                          store.status === "critical" ? "text-red-400" : 
                          store.status === "warning" ? "text-yellow-400" : "text-green-400"
                        }`}>
                          {store.estimated_impact > 0 ? `-${formatCurrency(store.estimated_impact, store.currency)}` : formatCurrency(0)}
                        </p>
                        <p className="text-xs text-text-muted">weekly impact</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Link to={`/dashboard?store=${store.store_id}`}>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-text-muted hover:text-white"
                            data-testid={`view-store-${store.store_id}`}
                          >
                            View Details
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Last Analyzed */}
                  {store.last_analyzed && (
                    <p className="text-xs text-text-muted/50 mt-3 pt-3 border-t border-[#262626]">
                      Last analyzed: {new Date(store.last_analyzed).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit"
                      })}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upgrade Prompt for users at store limit */}
        {billingStatus && portfolio?.total_stores >= billingStatus.store_limit && billingStatus.plan !== "agency" && (
          <Card className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border-[#D4F34A]/20 mt-8">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">Need to manage more stores?</h3>
                <p className="text-sm text-text-muted">
                  Upgrade to Agency to connect up to 15 stores and unlock agency features.
                </p>
              </div>
              <Link to="/settings">
                <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full">
                  Upgrade Plan
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PortfolioDashboard;
